/**
 * Piano Custom Filters
 * ====================
 * Client-side filtering for piano/keyboard collections.
 * Bypasses S&D 25-filter limit by filtering product cards directly in DOM.
 */
(function () {
  'use strict';

  const dataEl = document.getElementById('PianoFilterData');
  if (!dataEl) return;

  let pianoData;
  try {
    pianoData = JSON.parse(dataEl.textContent);
  } catch (e) {
    console.error('Piano filters: invalid JSON data');
    return;
  }

  const activeFilters = {}; // { field_key: Set([values]) }
  const productGrid = document.getElementById('ProductsList');
  const productCount = document.getElementById('ProductCount');
  const activeFiltersContainer = document.getElementById('PianoActiveFilters');
  const activeFilterTags = document.getElementById('PianoActiveFilterTags');
  const clearAllBtn = document.getElementById('PianoClearAllFilters');

  if (!productGrid) return;

  // Map product IDs to their DOM elements (the f-column wrapper)
  const productElements = {};
  const columns = productGrid.querySelectorAll('.f-column.product-column');
  columns.forEach(function (col) {
    const card = col.querySelector('.product-card');
    if (!card) return;
    // Find product ID from any child element that has data-product-id,
    // or from the product URL
    const compareCheckbox = card.querySelector('[data-product-id]');
    if (compareCheckbox) {
      const pid = compareCheckbox.getAttribute('data-product-id');
      productElements[pid] = col;
    } else {
      // Fallback: extract from product link
      const link = card.querySelector('a[href*="/products/"]');
      if (link) {
        // We'll use the href as a key
        col.setAttribute('data-piano-card', 'true');
      }
    }
  });

  // Also try to map by product ID from the JSON data
  // If compare checkbox isn't present, match by product order
  if (Object.keys(productElements).length === 0) {
    const productIds = Object.keys(pianoData.products);
    let idx = 0;
    columns.forEach(function (col) {
      if (col.querySelector('.product-card') && idx < productIds.length) {
        productElements[productIds[idx]] = col;
        idx++;
      }
    });
  }

  function getActiveFilterCount() {
    let count = 0;
    Object.values(activeFilters).forEach(function (s) {
      count += s.size;
    });
    return count;
  }

  function applyFilters() {
    const hasActiveFilters = getActiveFilterCount() > 0;
    let visibleCount = 0;
    let totalCount = 0;

    // Also count image cards that should remain visible
    const imageColumns = productGrid.querySelectorAll('.f-column.image-column');

    Object.keys(productElements).forEach(function (pid) {
      const el = productElements[pid];
      const productMeta = pianoData.products[pid];
      totalCount++;

      if (!hasActiveFilters) {
        el.style.display = '';
        visibleCount++;
        return;
      }

      if (!productMeta) {
        el.style.display = 'none';
        return;
      }

      // Product must match ALL active filter groups (AND between groups)
      // Within a group, product must match ANY selected value (OR within group)
      let matches = true;
      Object.keys(activeFilters).forEach(function (fieldKey) {
        const selectedValues = activeFilters[fieldKey];
        if (selectedValues.size === 0) return;

        const productValue = productMeta[fieldKey] || '';
        if (!selectedValues.has(productValue)) {
          matches = false;
        }
      });

      if (matches) {
        el.style.display = '';
        visibleCount++;
      } else {
        el.style.display = 'none';
      }
    });

    // Hide/show image cards (promotional blocks) based on filter state
    if (hasActiveFilters) {
      imageColumns.forEach(function (ic) {
        ic.style.display = 'none';
      });
    } else {
      imageColumns.forEach(function (ic) {
        ic.style.display = '';
      });
    }

    // Update product count display
    if (productCount) {
      if (hasActiveFilters) {
        const totalProducts = Object.keys(pianoData.products).length;
        productCount.textContent = visibleCount + ' / ' + totalProducts + ' products';
      } else {
        // Restore original count text
        const totalProducts = Object.keys(pianoData.products).length;
        productCount.textContent = totalProducts + ' products';
      }
    }

    // Update filter value counts
    updateFilterCounts();

    // Update active filters display
    updateActiveFiltersDisplay();

    // Update URL params for shareability
    updateURL();
  }

  function updateFilterCounts() {
    const hasActiveFilters = getActiveFilterCount() > 0;
    if (!hasActiveFilters) {
      // Restore original counts
      document.querySelectorAll('.piano-filter-count').forEach(function (el) {
        el.textContent = el.getAttribute('data-original-count');
      });
      // Re-enable all checkboxes
      document.querySelectorAll('.piano-filter-checkbox').forEach(function (cb) {
        cb.disabled = false;
        cb.closest('li').classList.remove('opacity-50');
      });
      return;
    }

    // For each filter field, count how many visible products have each value
    pianoData.fields.forEach(function (field) {
      const fieldKey = field.key;
      field.values.forEach(function (valObj, idx) {
        const checkboxId = 'piano-filter-' + fieldKey + '-' + (idx + 1);
        const checkbox = document.getElementById(checkboxId);
        if (!checkbox) return;

        const countEl = checkbox.closest('li').querySelector('.piano-filter-count');
        if (!countEl) return;

        // Count products that match current filters PLUS this value
        let count = 0;
        Object.keys(pianoData.products).forEach(function (pid) {
          const meta = pianoData.products[pid];
          let matches = true;

          Object.keys(activeFilters).forEach(function (fk) {
            if (fk === fieldKey) return; // Skip the current field for counting
            const selectedValues = activeFilters[fk];
            if (selectedValues.size === 0) return;
            if (!selectedValues.has(meta[fk] || '')) {
              matches = false;
            }
          });

          if (matches && meta[fieldKey] === valObj.value) {
            count++;
          }
        });

        countEl.textContent = count;

        // Disable checkboxes with 0 count (unless already checked)
        if (count === 0 && !checkbox.checked) {
          checkbox.disabled = true;
          checkbox.closest('li').classList.add('opacity-50');
        } else {
          checkbox.disabled = false;
          checkbox.closest('li').classList.remove('opacity-50');
        }
      });
    });
  }

  function updateActiveFiltersDisplay() {
    if (!activeFiltersContainer || !activeFilterTags) return;

    const hasActiveFilters = getActiveFilterCount() > 0;
    if (!hasActiveFilters) {
      activeFiltersContainer.classList.add('hidden');
      return;
    }

    activeFiltersContainer.classList.remove('hidden');
    let html = '';

    Object.keys(activeFilters).forEach(function (fieldKey) {
      activeFilters[fieldKey].forEach(function (val) {
        html += '<button type="button" class="piano-remove-filter btn btn--outline btn--small" '
          + 'data-field="' + fieldKey + '" data-value="' + val.replace(/"/g, '&quot;') + '" '
          + 'style="font-size: 0.75rem; padding: 0.25rem 0.5rem; border-radius: 2rem; display: inline-flex; align-items: center; gap: 0.25rem;">'
          + '<span>' + val + '</span>'
          + '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M9 3L3 9M3 3l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
          + '</button> ';
      });
    });

    activeFilterTags.innerHTML = html;

    // Bind remove buttons
    activeFilterTags.querySelectorAll('.piano-remove-filter').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const field = this.getAttribute('data-field');
        const value = this.getAttribute('data-value');
        if (activeFilters[field]) {
          activeFilters[field].delete(value);
          if (activeFilters[field].size === 0) {
            delete activeFilters[field];
          }
        }
        // Uncheck the corresponding checkbox
        document.querySelectorAll('.piano-filter-checkbox[data-piano-field="' + field + '"]').forEach(function (cb) {
          if (cb.getAttribute('data-piano-value') === value) {
            cb.checked = false;
          }
        });
        applyFilters();
      });
    });
  }

  function updateURL() {
    const url = new URL(window.location.href);
    // Remove existing piano filter params
    Array.from(url.searchParams.keys()).forEach(function (key) {
      if (key.startsWith('pf_')) {
        url.searchParams.delete(key);
      }
    });

    // Add current filters
    Object.keys(activeFilters).forEach(function (fieldKey) {
      activeFilters[fieldKey].forEach(function (val) {
        url.searchParams.append('pf_' + fieldKey, val);
      });
    });

    window.history.replaceState({}, '', url.toString());
  }

  function restoreFromURL() {
    const url = new URL(window.location.href);
    url.searchParams.forEach(function (val, key) {
      if (key.startsWith('pf_')) {
        const fieldKey = key.substring(3);
        if (!activeFilters[fieldKey]) {
          activeFilters[fieldKey] = new Set();
        }
        activeFilters[fieldKey].add(val);

        // Check the corresponding checkbox
        document.querySelectorAll('.piano-filter-checkbox[data-piano-field="' + fieldKey + '"]').forEach(function (cb) {
          if (cb.getAttribute('data-piano-value') === val) {
            cb.checked = true;
          }
        });
      }
    });

    if (getActiveFilterCount() > 0) {
      applyFilters();
    }
  }

  // Bind checkbox change events
  document.querySelectorAll('.piano-filter-checkbox').forEach(function (checkbox) {
    checkbox.addEventListener('change', function () {
      const field = this.getAttribute('data-piano-field');
      const value = this.getAttribute('data-piano-value');

      if (!activeFilters[field]) {
        activeFilters[field] = new Set();
      }

      if (this.checked) {
        activeFilters[field].add(value);
      } else {
        activeFilters[field].delete(value);
        if (activeFilters[field].size === 0) {
          delete activeFilters[field];
        }
      }

      applyFilters();
    });
  });

  // Clear all button
  if (clearAllBtn) {
    clearAllBtn.addEventListener('click', function () {
      Object.keys(activeFilters).forEach(function (key) {
        delete activeFilters[key];
      });
      document.querySelectorAll('.piano-filter-checkbox').forEach(function (cb) {
        cb.checked = false;
      });
      applyFilters();
    });
  }

  // Restore filters from URL on load
  restoreFromURL();
})();
